import { intersectionWith } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Tabs } from 'common/constants';
import { LockStatus } from 'pages/AuthorizePaymentPage/types';
import { getSelectedTab } from 'pages/LandingPage/components/LandingPageHeader/slice';
import {
  getInvoiceIdsToSelect,
  getNumberFromCurrency,
  getVendorsTableDataId
} from 'pages/VendorsTablePage/components/VendorsTable/helpers';
import {
  getIsRowSelected,
  getSelectedRows,
  selectInvoiceData
} from 'pages/VendorsTablePage/components/VendorsTable/slice';
import {
  CheckboxRendererResult,
  VendorsTableDataType
} from 'pages/VendorsTablePage/components/VendorsTable/types';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import type { IRowNode } from 'ag-grid-community';

export const useHighVolumeCheckboxRenderer = (
  node: IRowNode<VendorsTableDataType>
): CheckboxRendererResult => {
  const { key } = node;
  const nodeData = node.data;
  const dispatch = useAppDispatch();
  const selectedTab = useAppSelector(getSelectedTab);

  const rowLabel = key || nodeData?.invoiceId || '';

  const parentNode = useMemo(() => (node.key ? node : node.parent), [node]);
  const childInvoices = useMemo(() => {
    if (!nodeData || !parentNode?.data) return [];
    const { invoices, ...rest } = parentNode.data;
    return invoices.map(invoice => ({
      ...rest,
      ...invoice,
      id: getVendorsTableDataId(nodeData, invoice),
      invoices: []
    }));
  }, [nodeData, parentNode?.data]);

  const selectedRows = useAppSelector(
    getSelectedRows(nodeData?.vendorNo ?? null)
  );

  const isChecked = useAppSelector(
    getIsRowSelected(key, nodeData?.vendorNo, nodeData?.invoiceId)
  );
  const isIndeterminate = useMemo(
    () =>
      nodeData?.invoices.length
        ? selectedRows.length !== nodeData.invoices.length
        : false,
    [nodeData?.invoices.length, selectedRows.length]
  );
  const isDisabled = useMemo(() => {
    if (key) {
      return (
        nodeData?.vendorOnHold ||
        childInvoices.every(
          ({ lockStatus, netAmount, onHold }) =>
            onHold ||
            getNumberFromCurrency(netAmount) < 0 ||
            (selectedTab === Tabs.ToBePaid && lockStatus === LockStatus.LOCKED)
        )
      );
    }
    if (nodeData?.vendorNo && nodeData.invoiceId) {
      const factor = isChecked ? -1 : 1;
      const selectedVendorTotal = selectedRows.reduce(
        (total, invoice) => total + getNumberFromCurrency(invoice.netAmount),
        0
      );
      return (
        nodeData.onHold ||
        parentNode?.data?.vendorOnHold ||
        selectedVendorTotal +
          factor * getNumberFromCurrency(nodeData.netAmount) <
          0 ||
        (selectedTab === Tabs.ToBePaid &&
          nodeData.lockStatus === LockStatus.LOCKED)
      );
    }
    return false;
  }, [
    childInvoices,
    isChecked,
    key,
    nodeData,
    parentNode?.data?.vendorOnHold,
    selectedRows,
    selectedTab
  ]);

  const handleChange = useCallback(
    ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>): void => {
      if (key) {
        const invoiceIdsToSelect = getInvoiceIdsToSelect(
          childInvoices,
          checked,
          selectedTab
        );
        const invoicesToSelect = intersectionWith(
          childInvoices,
          invoiceIdsToSelect,
          (invoice, invoiceId) => invoice.id === invoiceId
        );
        dispatch(
          selectInvoiceData({ invoices: invoicesToSelect, selected: checked })
        );
      } else if (nodeData?.id) {
        dispatch(
          selectInvoiceData({ invoices: [nodeData], selected: checked })
        );
      }
    },
    [childInvoices, dispatch, nodeData, key, selectedTab]
  );

  return {
    isDisabled,
    isChecked,
    isIndeterminate,
    rowLabel,
    handleChange
  };
};
